import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import axios from "axios";
import { toast } from "sonner";

const CartContext = createContext(null);

const CART_STORAGE_KEY = "buymoreeasy_cart";
const API_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8002") + "/api";

export const CartProvider = ({ children }) => {
  const { user, token, isAuthenticated, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [savedForLater, setSavedForLater] = useState([]);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [selectedShipping, setSelectedShipping] = useState({});
  const [deliveryModes, setDeliveryModes] = useState({}); // { [productId]: mode }
  const [isLoaded, setIsLoaded] = useState(false);

  // Purge selectedShipping entries for stores no longer in the cart
  useEffect(() => {
    if (!isLoaded) return;
    const activeStoreIds = new Set(
      items.map(item => item.product?.store?.id).filter(Boolean)
    );
    setSelectedShipping(prev => {
      const cleaned = Object.fromEntries(
        Object.entries(prev).filter(([id]) => activeStoreIds.has(id))
      );
      // Only trigger a re-render if something actually changed
      return Object.keys(cleaned).length !== Object.keys(prev).length ? cleaned : prev;
    });
  }, [items, isLoaded]);

  // Auto-assign default delivery modes when items change
  useEffect(() => {
    if (items.length > 0) {
      setDeliveryModes(prev => {
        const updated = { ...prev };
        let changed = false;
        items.forEach(item => {
          if (item.product?.id && !updated[item.product.id]) {
            const modes = item.product.allowed_delivery_types || [];
            if (modes.length > 0) {
              updated[item.product.id] = modes[0];
              changed = true;
            }
          }
        });
        return changed ? updated : prev;
      });
    }
  }, [items]);

  // Helper to map backend item to frontend structure
  const mapBackendItemToFrontend = (backendItem) => {
    let parsedBookingDate = backendItem.booking_date;
    try {
      if (parsedBookingDate && typeof parsedBookingDate === 'string' && (parsedBookingDate.startsWith('{') || parsedBookingDate.startsWith('['))) {
        parsedBookingDate = JSON.parse(parsedBookingDate);
      }
    } catch (e) {
      console.warn("Failed to parse bookingDate", e);
    }

    return {
      cartItemId: backendItem.id,
      product: backendItem.product,
      quantity: backendItem.quantity,
      variants: backendItem.variants,
      bookingDate: parsedBookingDate,
      // Preserve other frontend expected fields if any
    };
  };

  // Fetch cart from backend
  const fetchBackendCart = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    try {
      const response = await axios.get(`${API_URL}/cart/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const backendItems = response.data.items;
      setItems(backendItems.map(mapBackendItemToFrontend));
    } catch (error) {
      console.error("Error fetching backend cart:", error);
      if (error.response?.status === 401 && logout) {
        logout();
      }
    }
  }, [isAuthenticated, logout, token]);

  // Set default address for logged-in users on page load or if selected address is no longer valid
  useEffect(() => {
    if (isAuthenticated && user?.saved_addresses?.length > 0 && isLoaded) {
      if (!shippingAddress) {
        const defaultAddr = user.saved_addresses.find(a => a.id === user.default_address_id);
        setShippingAddress(defaultAddr || user.saved_addresses[0]);
      } else {
        // Double check if shippingAddress still exists in user saved addresses
        // (to handle account switching or address deletion)
        const exists = user.saved_addresses.find(a => a.id === shippingAddress.id || (a.address === shippingAddress.address && a.city === shippingAddress.city));
        if (!exists) {
          const defaultAddr = user.saved_addresses.find(a => a.id === user.default_address_id);
          setShippingAddress(defaultAddr || user.saved_addresses[0]);
        }
      }
    }
  }, [isAuthenticated, user, shippingAddress, isLoaded]);

  // Initial Load
  useEffect(() => {
    const loadCart = async () => {
      setIsLoaded(false);
      try {
        const savedCart = localStorage.getItem(CART_STORAGE_KEY);
        let localShippingAddress = null;
        let localSelectedShipping = {};

        if (savedCart) {
          const parsed = JSON.parse(savedCart);
          localShippingAddress = parsed.shippingAddress || null;
          localSelectedShipping = parsed.selectedShipping || {};
          if (parsed.deliveryModes) setDeliveryModes(parsed.deliveryModes);
          if (parsed.savedForLater) setSavedForLater(parsed.savedForLater);
        }

        if (isAuthenticated) {
          // If logged in, prioritize backend cart items
          await fetchBackendCart();
          // But keep shipping address from local storage if it was set in this session
          setShippingAddress(localShippingAddress);
          setSelectedShipping(localSelectedShipping);
        } else {
          // Load everything from LocalStorage
          if (savedCart) {
            const parsed = JSON.parse(savedCart);
            const loadedItems = Array.isArray(parsed.items) ? parsed.items : [];
            const validItems = loadedItems.filter(item => item && item.product && item.product.id);
            setItems(validItems);
            setShippingAddress(localShippingAddress);
            setSelectedShipping(localSelectedShipping);
          }
        }
      } catch (error) {
        console.error("Error loading cart:", error);
      } finally {
        setIsLoaded(true);
      }
    };

    loadCart();
  }, [isAuthenticated, fetchBackendCart]);

  // Sync to LocalStorage
  useEffect(() => {
    if (!isLoaded) return;
    // We save shipping information even for authenticated users to persist session choice across reloads

    try {
      const storageItems = isAuthenticated ? [] : items.map(item => {
        if (!item.product) return item;
        return {
          ...item,
          product: {
            ...item.product,
            // Prevent QuotaExceededError by omitting large data strings
            images: item.product.images?.map(img => img?.length > 1000 ? img.substring(0, 50) + '...[truncated]' : img),
            description: item.product.description?.substring(0, 100)
          }
        };
      });

      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        items: storageItems,
        shippingAddress,
        selectedShipping,
        deliveryModes,
        savedForLater,
      }));
    } catch (error) {
      console.error("Error saving cart locally:", error);
      if (error.name === "QuotaExceededError") {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
          items: [],
          shippingAddress: null,
          selectedShipping: {},
          deliveryModes: {},
          savedForLater: [],
        }));
      }
    }
  }, [items, savedForLater, shippingAddress, selectedShipping, isLoaded, isAuthenticated]);

  const addItem = useCallback(async (product, quantity = 1, bookingDate = null, variants = null) => {
    if (isAuthenticated) {
      // Backend Sync
      try {
        await axios.post(`${API_URL}/cart/items`, {
          product_id: product.id,
          quantity,
          variants,
          booking_date: bookingDate ? (typeof bookingDate === 'object' ? JSON.stringify(bookingDate) : bookingDate) : null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchBackendCart();
        return true;
      } catch (error) {
        console.error("Add to cart error:", error);
        toast.error(error.response?.data?.detail || "Erreur lors de l'ajout au panier");
        return false;
      }
    } else {
      // Local Logic
      setItems((prev) => {
        const isSameItem = (item) => {
          const sameId = item.product.id === product.id;
          const sameDate = JSON.stringify(item.bookingDate) === JSON.stringify(bookingDate);
          const sameVariants = JSON.stringify(item.variants) === JSON.stringify(variants);
          return sameId && sameDate && sameVariants;
        };

        const existing = prev.find(isSameItem);

        if (existing) {
          const newTotal = existing.quantity + quantity;
          if (newTotal > product.stock_quantity) {
            toast.error(`La quantité demandée dépasse le stock disponible (${product.stock_quantity}).`);
          }
          return prev.map((item) =>
            isSameItem(item)
              ? { ...item, quantity: Math.min(newTotal, product.stock_quantity) }
              : item
          );
        }

        if (quantity > product.stock_quantity) {
          toast.error(`La quantité demandée dépasse le stock disponible (${product.stock_quantity}).`);
          return [...prev, { product, quantity: product.stock_quantity, bookingDate, variants }];
        }
        return [...prev, { product, quantity, bookingDate, variants }];
      });
      return true;
    }
  }, [isAuthenticated, fetchBackendCart, token]);

  const removeItem = useCallback(async (productId, variants = null) => {
    if (isAuthenticated) {
      // For backend, we really need the CartItem ID. 
      // But UI might pass productId. We need to find the item in current state.
      // Or change UI to pass cartItemId. 
      // Fallback: Find item in local state matching criteria.

      const itemToRemove = items.find(item =>
        item.product.id === productId &&
        (!variants || JSON.stringify(item.variants) === JSON.stringify(variants))
      );

      if (itemToRemove && itemToRemove.cartItemId) {
        try {
          await axios.delete(`${API_URL}/cart/items/${itemToRemove.cartItemId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          await fetchBackendCart();
        } catch (error) {
          console.error("Remove item error:", error);
        }
      }
    } else {
      setItems((prev) => prev.filter((item) => item.product.id !== productId));
    }
  }, [isAuthenticated, items, fetchBackendCart, token]);

  const updateQuantity = useCallback(async (productId, quantity, variants = null) => {
    if (quantity <= 0) {
      removeItem(productId, variants);
      return;
    }

    let finalQuantity = quantity;
    const itemToUpdate = items.find(item =>
      item.product.id === productId &&
      (!variants || JSON.stringify(item.variants) === JSON.stringify(variants))
    );

    if (itemToUpdate) {
      const isSupplierStore = itemToUpdate.product.store?.store_type?.toUpperCase() === 'SUPPLIER';
      const minQty = (itemToUpdate.product.is_resell || !isSupplierStore) ? 1 : (itemToUpdate.product.min_wholesale_quantity || 1);
      if (quantity > 0 && quantity < minQty) {
        finalQuantity = minQty;
        toast.error(`La quantité minimum pour ce produit est de ${minQty}.`);
      }
    }

    if (isAuthenticated && itemToUpdate && itemToUpdate.cartItemId) {
      try {
        await axios.put(`${API_URL}/cart/items/${itemToUpdate.cartItemId}`, { quantity: finalQuantity }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchBackendCart();
      } catch (error) {
        console.error("Update quantity error:", error);
        if (error.response?.data?.detail) {
          toast.error(error.response.data.detail);
        } else {
          toast.error("Échec de la mise à jour de la quantité.");
        }
      }
    } else {
      setItems((prev) =>
        prev.map((item) =>
          item.product.id === productId ? { ...item, quantity: finalQuantity } : item
        )
      );
    }
  }, [isAuthenticated, items, removeItem, fetchBackendCart, token]);

  const clearCart = useCallback(async () => {
    if (isAuthenticated) {
      try {
        await axios.delete(`${API_URL}/cart/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setItems([]);
      } catch (error) {
        console.error("Clear cart error:", error);
      }
    } else {
      setItems([]);
      setSelectedShipping({});
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, [isAuthenticated, token]);

  // Group items by store (Same logic as before)
  const itemsByStore = items.reduce((acc, item) => {
    // Check if product exists (safety)
    if (!item.product) return acc;

    const storeId = item.product.store?.id || "unknown";
    const storeName = item.product.store?.name || "Boutique inconnue";

    if (!acc[storeId]) {
      acc[storeId] = {
        storeId,
        storeName,
        items: [],
        subtotal: 0
      };
    }
    acc[storeId].items.push(item);

    let price = item.product.discount_price || item.product.price;
    if (item.variants) {
      Object.values(item.variants).forEach(v => {
        if (v && v.price_mod) price += v.price_mod;
      });
    }

    acc[storeId].subtotal += price * item.quantity;
    return acc;
  }, {});

  const subtotal = items.reduce((sum, item) => {
    if (!item.product) return sum;
    let price = item.product.discount_price || item.product.price;
    if (item.variants) {
      Object.values(item.variants).forEach(v => {
        if (v && v.price_mod) price += v.price_mod;
      });
    }
    return sum + price * item.quantity;
  }, 0);

  const taxes = items.reduce((sum, item) => {
    if (!item.product) return sum;
    let price = item.product.discount_price || item.product.price;
    if (item.variants) {
      Object.values(item.variants).forEach(v => {
        if (v && v.price_mod) price += v.price_mod;
      });
    }
    const taxRate = parseFloat(item.product.tax_rate) || 0;
    return sum + (price * item.quantity * (taxRate / 100));
  }, 0);

  // Only count shipping for stores that currently have items in the cart.
  // Stale entries from previous sessions (different stores) are ignored.
  const shippingTotal = Object.entries(selectedShipping).reduce(
    (sum, [storeId, shipping]) => {
      if (!itemsByStore[storeId]) return sum; // skip stale/removed store
      return sum + (shipping?.price || 0);
    },
    0
  );

  const serviceFees = items.reduce((sum, item) => sum + (item.bookingDate?.travelFee || 0), 0);

  const total = subtotal + shippingTotal + serviceFees + taxes;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const updateShippingForStore = useCallback((storeId, shippingOption) => {
    setSelectedShipping((prev) => ({
      ...prev,
      [storeId]: shippingOption
    }));
  }, []);

  const updateDeliveryMode = useCallback((productId, mode) => {
    setDeliveryModes((prev) => ({
      ...prev,
      [productId]: mode
    }));
    // NOTE: Shipping reset is handled by OrderPreviewPage when it recalculates.
    // Do NOT reset selectedShipping here — it causes checkout to show a wrong price.
  }, []);

  // ── Garder pour plus tard ────────────────────────────────────────────────────

  const saveForLater = useCallback(async (productId) => {
    const item = items.find(i => i.product?.id === productId);
    if (!item) return;
    setSavedForLater(prev => {
      // Replace if already saved (update qty/variants)
      const exists = prev.find(s => s.product?.id === productId);
      if (exists) return prev.map(s => s.product?.id === productId ? item : s);
      return [...prev, item];
    });
    await removeItem(productId);
  }, [items, removeItem]);

  const moveToCart = useCallback(async (productId) => {
    const saved = savedForLater.find(s => s.product?.id === productId);
    if (!saved) return;
    setSavedForLater(prev => prev.filter(s => s.product?.id !== productId));
    await addItem(saved.product, saved.quantity, saved.bookingDate, saved.variants);
  }, [savedForLater, addItem]);

  const removeSaved = useCallback((productId) => {
    setSavedForLater(prev => prev.filter(s => s.product?.id !== productId));
  }, []);

  return (
    <CartContext.Provider
      value={{
        items,
        savedForLater,
        saveForLater,
        moveToCart,
        removeSaved,
        itemsByStore,
        addItem,
        addToCart: addItem,
        removeItem,
        updateQuantity,
        clearCart,
        subtotal,
        taxes,
        shippingTotal,
        serviceFees,
        total,
        itemCount,
        shippingAddress,
        setShippingAddress,
        selectedShipping,
        updateShippingForStore,
        deliveryModes,
        updateDeliveryMode
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};
